import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Icon,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    THEMES,
    useSidebar,
    useTheme,
} from "@elit-hair/elit-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertCircle, TbCode, TbSettings } from "react-icons/tb";

import { HeaderStatusDropdown } from "@/components/header/status-dropdown";
import { BrandSelector } from "@/components/sidebar/brand-selector";
import { NavigationItems } from "@/components/sidebar/navigation-items";
import { PhoneNumberSelector } from "@/components/sidebar/phone-number-selector";
import { SidebarEdgeToggle } from "@/components/sidebar/sidebar-edge-toggle";
import { SidebarLink } from "@/components/sidebar/sidebar-link";
import { SidebarUserButton } from "@/components/sidebar/sidebar-user-button";
import { ENV } from "@/constants/env";
import { PERMISSIONS } from "@/constants/permissions";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { useColorSchemeChange } from "@/hooks/use-color-scheme-change";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { RolePreviewSelect } from "@/pages/home/components/role-dashboard/role-preview-select";
import { useRemoteConfigInput } from "@/pages/settings/utils/use-remote-config-input";
import { useCall } from "@/providers/hooks/use-call";
import { PermissionNames, usePermissions } from "@/providers/hooks/use-permissions";
import { useSingleInstanceContext } from "@/providers/single-instance-provider";
import { APP_ROUTES_TO } from "@/utils/routes";

export function SidebarLeft() {
    const { t } = useTranslation();
    const { userData, handleLogout } = useAuth();
    const { permissionCodes } = useDashboardRole();
    const { permissions, openModal } = usePermissions();
    const { isMultipleInstances } = useSingleInstanceContext();
    const { state } = useSidebar();
    const { isCallActive, selectedBrand, setSelectedBrand } = useCall();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    const { theme, selectedColorScheme, toggleTheme, colorSchemes } = useTheme();

    const { onChange: setTheme } = useRemoteConfigInput<THEMES>(SETTINGS_KEYS.THEME);
    const handleColorSchemeChange = useColorSchemeChange();

    // Handler to change elit-ui provider & remote config
    const handleThemeToggle = useCallback(() => {
        toggleTheme();
        setTheme(theme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
    }, [toggleTheme, setTheme]);

    const navigationItems = [
        {
            path: "/settings",
            name: t("settings"),
            icon: TbSettings,
        },
    ];

    const missingPermissions =
        permissions[PermissionNames.MICROPHONE] !== "granted" ||
        permissions[PermissionNames.NOTIFICATIONS] !== "granted";

    const canAccessDeveloper = checkPermissions(permissionCodes, [PERMISSIONS.CALL_SVC_ADMIN_BASE]);

    return (
        <Sidebar side="left" variant="inset" collapsible="icon">
            <SidebarHeader>
                <BrandSelector
                    selectedBrand={selectedBrand}
                    onBrandChange={setSelectedBrand}
                    isCollapsed={state === "collapsed"}
                />
                <PhoneNumberSelector isCollapsed={state === "collapsed"} />
                <SidebarSeparator />
            </SidebarHeader>
            <SidebarContent className="group-data-[collapsible=icon]:overflow-auto group-data-[collapsible=icon]:overflow-x-hidden">
                <NavigationItems />

                {isMultipleInstances && (
                    <SidebarGroup>
                        <SidebarSeparator />
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    className="text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20 w-full flex items-center justify-start gap-2 min-h-0 px-4 py-2.5 rounded-lg"
                                    tooltip={state === "collapsed" ? t("multiple_instances_open") : undefined}
                                >
                                    <Icon icon={TbAlertCircle} />
                                    <span className="text-sm font-medium group-data-[collapsible=icon]:hidden">
                                        {t("multiple_instances")}
                                    </span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroup>
                )}

                {missingPermissions && (
                    <>
                        <SidebarSeparator />
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild className="text-destructive hover:text-destructive">
                                            <button type="button" onClick={openModal}>
                                                <TbAlertCircle />
                                                <span>{t("fix_permissions")}</span>
                                            </button>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </>
                )}
            </SidebarContent>

            <SidebarFooter>
                <SidebarMenu>
                    {canAccessDeveloper ? (
                        <>
                            <SidebarSeparator />
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    asChild
                                    className="w-full justify-start"
                                    tooltip={state === "collapsed" ? t("developer") : undefined}
                                >
                                    <SidebarLink to={APP_ROUTES_TO.DEVELOPER} aria-label={t("developer")}>
                                        <Icon icon={TbCode} />
                                        <span className="truncate group-data-[collapsible=icon]:hidden">
                                            {t("developer")}
                                        </span>
                                    </SidebarLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </>
                    ) : null}
                    <SidebarSeparator />
                    <SidebarUserButton
                        userName={userData?.name || "User"}
                        status={
                            <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
                                <RolePreviewSelect />
                                <HeaderStatusDropdown />
                            </div>
                        }
                        isLogoutDisabled={isCallActive()}
                        navigationItems={navigationItems}
                        theme={theme}
                        onToggleTheme={handleThemeToggle}
                        colorScheme={selectedColorScheme}
                        colorSchemeOptions={colorSchemes}
                        onColorSchemeChange={handleColorSchemeChange}
                        onLogout={() => setShowLogoutDialog(true)}
                        logoutDialog={
                            <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>{t("logout_confirmation")}</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {t("logout_confirmation_description")}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => {
                                                handleLogout();
                                                setShowLogoutDialog(false);
                                            }}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                            {t("logout")}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        }
                    />
                </SidebarMenu>
                <div className="pointer-events-none fixed bottom-0 left-0 z-maximum opacity-60">
                    <div className="rounded-sm bg-background px-2 py-0.5 text-[10px] leading-none text-muted-foreground shadow-md">
                        v{ENV.RELEASE_VERSION}
                    </div>
                </div>
            </SidebarFooter>
            <SidebarEdgeToggle />
        </Sidebar>
    );
}
